import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis,
} from "recharts";
import { DownloadSimple, Spinner, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const Metric = ({ label, value, hint }) => (
  <div className="border border-border bg-card p-5 min-h-[116px] flex flex-col justify-between">
    <div className="label-eyebrow">{label}</div>
    <div>
      <div className="font-display text-3xl lg:text-4xl font-black tracking-tight leading-none">{value}</div>
      {hint && <div className="text-xs text-muted-foreground font-mono-data mt-2">{hint}</div>}
    </div>
  </div>
);

const fmt = (value, digits = 2, suffix = "") => (
  value === null || value === undefined || Number.isNaN(Number(value))
    ? "—"
    : `${Number(value).toFixed(digits)}${suffix}`
);

const qualityClasses = {
  Boa: "border-[hsl(var(--success))]/50 text-[hsl(var(--success))]",
  Aceitável: "border-primary/50 text-primary",
  Revisar: "border-[hsl(var(--warning))]/60 text-[hsl(var(--warning))]",
  Crítica: "border-destructive/60 text-destructive",
};

function ItemDetail({ item }) {
  if (!item) return null;
  return (
    <div className="border border-border bg-card">
      <div className="px-5 py-4 border-b border-border flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <div className="label-eyebrow">análise detalhada</div>
          <h3 className="font-display text-3xl font-black mt-1">{item.label} · gabarito {item.answer_key}</h3>
        </div>
        <span className={`border px-3 py-1 text-xs font-mono-data uppercase tracking-widest ${qualityClasses[item.quality] || "border-border"}`}>
          {item.quality}
        </span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-border">
        {[
          ["acerto", fmt(item.correct_percentage, 1, "%")],
          ["ponto-bisserial", fmt(item.point_biserial, 3)],
          ["D grupos 27%", fmt(item.discrimination_27, 3)],
          ["TRI a", fmt(item.irt_discrimination_a, 3)],
          ["TRI b", fmt(item.irt_difficulty_b, 3)],
          ["ajuste TRI", item.irt_fit || "—"],
        ].map(([label, value]) => (
          <div key={label} className="bg-card p-4">
            <div className="label-eyebrow">{label}</div>
            <div className="font-display text-xl font-black mt-2">{value}</div>
          </div>
        ))}
      </div>
      {!!item.flags?.length && (
        <div className="p-4 border-t border-border bg-[hsl(var(--warning))]/5 text-sm">
          <strong>Atenção:</strong> {item.flags.join(" · ")}
        </div>
      )}
      <div className="p-5">
        <div className="label-eyebrow mb-3">poder das alternativas e distratores</div>
        <div className="grid md:grid-cols-5 gap-3">
          {(item.options || []).map((option) => (
            <div key={option.value} className={`border p-4 ${option.is_key ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/5" : "border-border"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-display text-2xl font-black">{option.option}</span>
                {option.is_key && <span className="label-eyebrow text-[hsl(var(--success))]">gabarito</span>}
                {option.is_distractor && (
                  <span className={`text-[10px] font-mono-data uppercase ${option.functional ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                    {option.functional ? "funcional" : "não funcional"}
                  </span>
                )}
              </div>
              <div className="text-sm mt-3"><strong>{option.count}</strong> marcação(ões) · {fmt(option.percentage, 1, "%")}</div>
              <div className="text-xs text-muted-foreground font-mono-data mt-2">
                r opção-total: {fmt(option.point_biserial, 3)}
              </div>
              <div className="text-xs text-muted-foreground font-mono-data mt-1">
                grupo inferior {fmt(option.low_group_percentage, 1, "%")} · superior {fmt(option.high_group_percentage, 1, "%")}
              </div>
              {option.reason && <div className="text-xs text-destructive mt-3">{option.reason}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PsychometricsPanel({ examId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState("");
  const [qualityFilter, setQualityFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [rankingMode, setRankingMode] = useState("tri");

  const load = useCallback(async (force = false) => {
    if (force) setRecalculating(true); else setLoading(true);
    setError("");
    try {
      const response = await api.get(`/exams/${examId}/psychometrics${force ? "?force=true" : ""}`);
      setData(response.data);
      if (!selectedQuestion && response.data?.items?.length) {
        setSelectedQuestion(response.data.items[0].question);
      }
      if (force) toast.success("Análise psicométrica recalculada");
    } catch (requestError) {
      const message = fmtError(requestError);
      setError(message);
      toast.error("Falha na análise psicométrica: " + message);
    } finally {
      setLoading(false);
      setRecalculating(false);
    }
  }, [examId, selectedQuestion]);

  useEffect(() => { load(false); }, [examId]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportReport = async () => {
    try {
      const response = await api.get(`/exams/${examId}/psychometrics.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `psicometria_${data?.exam?.name?.replace(/[^a-z0-9]+/gi, "_") || "prova"}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      toast.error("Falha ao exportar: " + fmtError(requestError));
    }
  };

  const items = useMemo(() => data?.items || [], [data?.items]);
  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase().replace(/^q/, "");
    return items.filter((item) => (
      (qualityFilter === "all" || item.quality === qualityFilter)
      && (!query || String(item.question).includes(query) || item.quality.toLowerCase().includes(query))
    ));
  }, [items, qualityFilter, search]);
  const selectedItem = items.find((item) => item.question === selectedQuestion) || filteredItems[0] || items[0];

  const qualityData = useMemo(() => {
    const counts = data?.summary?.quality_counts || {};
    return ["Boa", "Aceitável", "Revisar", "Crítica"].map((quality) => ({
      quality,
      count: Number(counts[quality] || 0),
    }));
  }, [data?.summary?.quality_counts]);

  const scoreComparison = useMemo(() => (
    (data?.students || []).filter((student) => student.tri_score !== undefined && student.tri_score !== null)
  ), [data?.students]);

  const rankedStudents = useMemo(() => {
    const rows = [...(data?.students || [])];
    if (rankingMode === "raw") return rows.sort((a, b) => (a.raw_rank || 99999) - (b.raw_rank || 99999));
    return rows.sort((a, b) => (a.tri_rank || 99999) - (b.tri_rank || 99999));
  }, [data?.students, rankingMode]);

  if (loading) {
    return (
      <div className="border border-border bg-card p-16 flex items-center justify-center gap-3 label-eyebrow">
        <Spinner size={20} className="animate-spin" /> calculando psicometria…
      </div>
    );
  }
  if (error) {
    return (
      <div className="border border-destructive/50 bg-destructive/5 p-8">
        <div className="font-display text-2xl font-black">Não foi possível calcular a psicometria.</div>
        <div className="text-sm text-muted-foreground mt-2">{error}</div>
        <Button className="rounded-none mt-5" onClick={() => load(true)}>Tentar novamente</Button>
      </div>
    );
  }

  const summary = data?.summary || {};
  if (!summary.sample_size) {
    return <div className="p-16 border border-border text-center label-eyebrow">nenhum cartão válido para análise psicométrica</div>;
  }

  return (
    <div className="space-y-6" data-testid="exam-psychometrics">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border border-border bg-card p-5">
        <div>
          <div className="label-eyebrow">teoria clássica dos testes + teoria de resposta ao item</div>
          <h2 className="font-display text-3xl font-black tracking-tight mt-1">Qualidade da prova e duas formas de nota</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-4xl">
            A nota por acertos permanece transparente. A nota TRI usa o modelo Rasch/1PL e a escala do Enamed: theta × 17,544 + 67,018, limitada entre 0 e 100; 60,0 indica proficiência.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button variant="outline" className="rounded-none" disabled={recalculating} onClick={() => load(true)}>
            {recalculating && <Spinner size={14} className="animate-spin mr-2" />} Recalcular
          </Button>
          <Button className="rounded-none" onClick={exportReport}>
            <DownloadSimple size={15} weight="bold" className="mr-2" /> Relatório psicométrico
          </Button>
        </div>
      </div>

      {!!data?.warnings?.length && (
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-5">
          <div className="flex items-center gap-2 font-semibold"><Warning size={20} weight="fill" className="text-[hsl(var(--warning))]" /> Cuidados de interpretação</div>
          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
            {data.warnings.map((warning) => <div key={warning}>• {warning}</div>)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Metric label="amostra" value={summary.sample_size} hint={`${summary.valid_items} questões válidas`} />
        <Metric label="KR-20" value={fmt(summary.kr20, 3)} hint={summary.reliability} />
        <Metric label="erro de medida" value={fmt(summary.standard_error_percentage, 1, "%")} hint="na escala por acertos" />
        <Metric label="itens bons" value={summary.good_items || 0} hint={`de ${summary.valid_items}`} />
        <Metric label="itens a revisar" value={(summary.review_items || 0) + (summary.critical_items || 0)} hint={`${summary.critical_items || 0} críticos`} />
        <Metric label="modelo TRI" value={summary.irt_model || "—"} hint={summary.irt_available ? (summary.irt_converged ? "convergência confirmada" : "convergência parcial") : "não calculado"} />
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <section className="border border-border bg-card p-5">
          <div className="label-eyebrow">qualidade das questões</div>
          <h3 className="font-display text-2xl font-black mt-1">Classificação geral</h3>
          <div className="h-[300px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={qualityData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="quality" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => [`${value} questão(ões)`, "Quantidade"]} />
                <Bar dataKey="count" name="Questões" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="border border-border bg-card p-5">
          <div className="label-eyebrow">acertos × TRI</div>
          <h3 className="font-display text-2xl font-black mt-1">Como as duas notas se relacionam</h3>
          {summary.irt_available ? (
            <div className="h-[300px] mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" dataKey="raw_score" name="Acertos" unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis type="number" dataKey="tri_score" name="Nota TRI" tick={{ fontSize: 11 }} />
                  <ZAxis range={[35, 35]} />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(value, name) => [fmt(value, 2, name === "Acertos" ? "%" : ""), name]} />
                  <Scatter data={scoreComparison} fill="hsl(var(--primary))" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground text-center px-8">
              A TRI ainda não está disponível para esta amostra. A análise clássica das questões continua válida.
            </div>
          )}
        </section>
      </div>

      {summary.irt_available && !!data?.test_information?.length && (
        <section className="border border-border bg-card p-5">
          <div className="label-eyebrow">precisão da TRI</div>
          <h3 className="font-display text-2xl font-black mt-1">Informação do teste ao longo da habilidade</h3>
          <p className="text-sm text-muted-foreground mt-2">Quanto maior a informação, menor o erro de medida naquela faixa de habilidade.</p>
          <div className="h-[330px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.test_information} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="tri_score" tick={{ fontSize: 11 }} label={{ value: "Nota TRI estilo Enamed", position: "insideBottom", offset: -2 }} />
                <YAxis yAxisId="info" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="error" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value, name) => [fmt(value, 3), name]} labelFormatter={(value) => `Nota TRI ${value}`} />
                <Legend />
                <Line yAxisId="info" type="monotone" dataKey="information" name="Informação" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                <Line yAxisId="error" type="monotone" dataKey="measurement_error" name="Erro em theta" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      <section className="border border-border bg-card overflow-hidden">
        <div className="p-5 border-b border-border flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="label-eyebrow">análise item a item</div>
            <h3 className="font-display text-2xl font-black mt-1">Dificuldade, discriminação e distratores</h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input className="rounded-none w-full sm:w-48" placeholder="Buscar questão" value={search} onChange={(event) => setSearch(event.target.value)} />
            <Select value={qualityFilter} onValueChange={setQualityFilter}>
              <SelectTrigger className="rounded-none w-full sm:w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as qualidades</SelectItem>
                <SelectItem value="Boa">Boas</SelectItem>
                <SelectItem value="Aceitável">Aceitáveis</SelectItem>
                <SelectItem value="Revisar">Revisar</SelectItem>
                <SelectItem value="Crítica">Críticas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <div className="min-w-[1180px] grid grid-cols-[80px_100px_110px_130px_120px_100px_110px_130px_1fr] gap-3 px-4 py-3 bg-muted/30 border-b border-border label-eyebrow sticky top-0 z-10">
            <div>Questão</div><div>Acerto</div><div>Dificuldade</div><div>Ponto-bisserial</div><div>D 27%</div><div>TRI a</div><div>TRI b</div><div>Distratores</div><div>Qualidade</div>
          </div>
          {filteredItems.map((item) => (
            <button key={item.question} onClick={() => setSelectedQuestion(item.question)} className={`w-full text-left min-w-[1180px] grid grid-cols-[80px_100px_110px_130px_120px_100px_110px_130px_1fr] gap-3 px-4 py-3 border-b border-border items-center hover:bg-muted/30 ${selectedItem?.question === item.question ? "bg-primary/5" : ""}`}>
              <div className="font-display text-lg font-black">{item.label}</div>
              <div className="font-mono-data">{fmt(item.correct_percentage, 1, "%")}</div>
              <div className="text-xs font-semibold">{item.difficulty}</div>
              <div className="font-mono-data">{fmt(item.point_biserial, 3)}</div>
              <div className="font-mono-data">{fmt(item.discrimination_27, 3)}</div>
              <div className="font-mono-data">{fmt(item.irt_discrimination_a, 3)}</div>
              <div className="font-mono-data">{fmt(item.irt_difficulty_b, 3)}</div>
              <div className="font-mono-data text-xs">{item.functional_distractors} funcionais · {item.non_functioning_distractors} fracos</div>
              <div><span className={`border px-2 py-1 text-[10px] font-mono-data uppercase ${qualityClasses[item.quality] || "border-border"}`}>{item.quality}</span></div>
            </button>
          ))}
        </div>
      </section>

      <ItemDetail item={selectedItem} />

      <section className="border border-border bg-card overflow-hidden">
        <div className="p-5 border-b border-border flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="label-eyebrow">duas classificações</div>
            <h3 className="font-display text-2xl font-black mt-1">Notas dos alunos por acertos e pela TRI</h3>
            <p className="text-sm text-muted-foreground mt-2">
              A nota individual usa Rasch/1PL, como no Enamed. A análise 2PL permanece disponível apenas para estudar a discriminação e a qualidade dos itens.
            </p>
          </div>
          <Select value={rankingMode} onValueChange={setRankingMode}>
            <SelectTrigger className="rounded-none w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tri">Ordenar pela TRI</SelectItem>
              <SelectItem value="raw">Ordenar por acertos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto max-h-[620px] overflow-y-auto">
          <div className="min-w-[1320px] grid grid-cols-[90px_90px_1fr_180px_100px_130px_110px_130px_140px_110px] gap-3 px-4 py-3 bg-muted/30 border-b border-border label-eyebrow sticky top-0 z-10">
            <div>Pos. TRI</div><div>Pos. acertos</div><div>Aluno</div><div>Turma</div><div>Acertos</div><div>Nota acertos</div><div>Theta</div><div>Nota TRI</div><div>Classificação</div><div>Percentil</div>
          </div>
          {rankedStudents.map((student) => (
            <div key={student.correction_id} className="min-w-[1320px] grid grid-cols-[90px_90px_1fr_180px_100px_130px_110px_130px_140px_110px] gap-3 px-4 py-3 border-b border-border items-center">
              <div className="font-display text-xl font-black">{student.tri_rank || "—"}</div>
              <div className="font-mono-data">{student.raw_rank || "—"}</div>
              <div><div className="font-semibold">{student.student_name}</div><div className="text-xs text-muted-foreground font-mono-data">{student.cpf || "CPF não identificado"}</div></div>
              <div className="text-sm">{student.class_name}</div>
              <div className="font-mono-data">{student.hits}/{student.total_valid}</div>
              <div className="font-mono-data font-bold">{fmt(student.raw_score, 2, "%")}</div>
              <div className="font-mono-data">{fmt(student.theta, 3)}</div>
              <div className="font-mono-data font-bold text-primary">{fmt(student.tri_score, 1)}</div>
              <div className={`text-xs font-semibold ${student.proficient ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{student.proficiency_status || "—"}</div>
              <div className="font-mono-data">{fmt(student.tri_percentile, 1, "%")}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="border border-border bg-muted/20 p-5 text-sm text-muted-foreground">
        <strong className="text-foreground">Interpretação:</strong> dificuldade e discriminação não devem ser usadas isoladamente. Uma questão difícil pode ser excelente se discriminar bem; uma questão fácil pode ser adequada para objetivos essenciais. A decisão final deve considerar conteúdo, clareza, currículo e evidências estatísticas em conjunto.
      </div>
    </div>
  );
}
